import { useEffect, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { tools, categories, type Tool, type Category } from '@/data/tools';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [search, setSearch] = useState('');

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  // Handle tool selection
  const handleSelectTool = (tool: Tool) => {
    if (tool.type === 'internal' && tool.path) {
      window.location.hash = tool.path;
    } else if (tool.type === 'external' && tool.url) {
      window.open(tool.url, '_blank', 'noopener,noreferrer');
    }
    onOpenChange(false);
  };

  // Get icon component
  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName];
    return Icon ? <Icon className='h-4 w-4' /> : null;
  };

  // Group tools by category (excluding 'all' and 'favorites')
  const groupedTools = categories
    .filter((cat) => cat.id !== 'all' && cat.id !== 'favorites')
    .map((category) => ({
      category,
      tools: tools.filter((tool) => tool.category === category.id),
    }))
    .filter((group) => group.tools.length > 0);

  // Filter tools based on search query
  const filteredGroups = groupedTools
    .map((group) => ({
      ...group,
      tools: group.tools.filter((tool) => {
        if (!search.trim()) return true;
        const query = search.toLowerCase();
        return tool.name.toLowerCase().includes(query) || tool.description.toLowerCase().includes(query);
      }),
    }))
    .filter((group) => group.tools.length > 0);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className='overflow-hidden p-0 max-w-2xl'>
        <Command
          shouldFilter={false}
          className='[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5'
        >
          <CommandInput
            placeholder='Search tools...'
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No tools found.</CommandEmpty>
            {filteredGroups.map((group, index) => (
              <div key={group.category.id}>
                {index > 0 && <CommandSeparator />}
                <CommandGroup heading={group.category.name}>
                  {group.tools.map((tool) => {
                    // Create searchable value with name, description, category for cmdk filtering
                    const searchValue = `${tool.name} ${tool.description} ${tool.category}`;

                    return (
                      <CommandItem
                        key={tool.id}
                        value={tool.id}
                        onSelect={() => handleSelectTool(tool)}
                        className='cursor-pointer'
                      >
                        {getIcon(tool.icon)}
                        <div className='flex flex-col'>
                          <span className='font-medium'>{tool.name}</span>
                          <span className='text-xs text-muted-foreground line-clamp-1'>{tool.description}</span>
                        </div>
                        {tool.type === 'external' && (
                          <LucideIcons.ExternalLink className='ml-auto h-3 w-3 text-muted-foreground' />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </div>
            ))}
          </CommandList>
          <div className='border-t p-2 text-xs text-muted-foreground text-center'>
            Navigate with <kbd className='px-1.5 py-0.5 rounded bg-muted font-mono'>↑↓</kbd> • Select with{' '}
            <kbd className='px-1.5 py-0.5 rounded bg-muted font-mono'>Enter</kbd> • Close with{' '}
            <kbd className='px-1.5 py-0.5 rounded bg-muted font-mono'>Esc</kbd>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
